import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { capitalize, isEqual, snakeCase } from 'lodash/fp';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect, { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import {
  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY,
  FieldSchemaEditorSupportedTypes,
  FieldSchemaRelationSchema,
  FocusReason,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { Identifier } from 'dashboard/models/identifier';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Hairline } from 'design-system/variables/effect';
import {
  FieldSchema,
  FieldSchemaArray,
  FieldSchemaBoolean,
  FieldSchemaChoices,
  FieldSchemaDate,
  FieldSchemaEmailAddress,
  FieldSchemaFile,
  FieldSchemaHash,
  FieldSchemaInteger,
  FieldSchemaMultiChoices,
  FieldSchemaNumber,
  FieldSchemaString,
  FieldSchemaType,
} from 'lib/fields';

export const DEFAULT_FIELD_SCHEMA_PANEL_WIDTH = 400;
export const SCROLLBAR_GUTTER_WIDTH = 15;

const FieldSchemaPanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: ${DEFAULT_FIELD_SCHEMA_PANEL_WIDTH}px;
  flex: none;
  padding: ${Spacing[5]};
  min-height: 100%;
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  display: flex;
  flex-direction: column;
  position: relative;
`;

const FieldConfigurationSection = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: ${Hairline.Top};
`;

const FieldConfigurationControl = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  box-shadow: ${Hairline.Top};
  padding: ${Spacing[4]} ${Spacing[5]};
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
`;

const FieldButtonGroup = styled(HStack)`
  width: fit-content;
`;

const InputLabel = styled(FormControlLabel)`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const StickerContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[2]};
`;

const RequireCheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
  color: ${Color.TextSecondary};
`;

const StyledAttributeGrid = styled('div')<{
  colCount?: number;
}>`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-row-gap: ${Spacing[4]};
  grid-template-columns: ${({ colCount }) => `repeat(${colCount ?? 3}, 1fr)`};

  & + & {
    padding-bottom: ${Spacing[5]};
  }
`;

const TooltipText = styled(Paragraph)`
  max-width: 300px;
`;

const SUPPORTED_FIELD_TYPE_OPTIONS = [
  FieldSchemaType.Array,
  FieldSchemaType.Boolean,
  FieldSchemaType.Date,
  FieldSchemaType.EmailAddress,
  FieldSchemaType.File,
  FieldSchemaType.Hash,
  FieldSchemaType.Integer,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Choices,
  FieldSchemaType.Number,
  FieldSchemaType.String,
  RelationSchemaType.RelationSchema,
];

const SUPPORTED_ARRAY_TYPES_OPTIONS = [
  FieldSchemaType.Boolean,
  FieldSchemaType.EmailAddress,
  FieldSchemaType.File,
  FieldSchemaType.Hash,
  FieldSchemaType.Integer,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

interface FieldSchemaPanelProps {
  allowRelation?: boolean;
  allowHash?: boolean;
  errorMessage?: string;
  fieldSchema: FieldSchemaEditorSupportedTypes;
  fieldSchemaPath?: string;
  relationObjectTypeOptions?: OptionType<string>[];
  identifierConfig?: Identifier;
  isComputedFieldsEnabled?: boolean;
  isRequirePresenceEnabled?: boolean;
  canConfigureIdentifier?: boolean;
  isTopLevelField?: boolean;
  isNewField: boolean;
  isSystemField?: boolean;
  onCancel: () => void;
  onEditing: (fieldSchema: FieldSchemaEditorSupportedTypes, identifierConfig?: Identifier) => void;
  onUpdate: (fieldSchema: FieldSchemaEditorSupportedTypes, identifierConfig?: Identifier) => void;
  selectReason: FocusReason;
  systemFieldSchemas?: FieldSchema[];
}

const FOCUS_REASON_TO_ACTION = {
  add: 'Add field',
  edit: 'Update field',
  move: 'Move field',
};

const hasComputedConfig = (
  schema: FieldSchemaEditorSupportedTypes,
): schema is FieldSchemaBoolean | FieldSchemaInteger | FieldSchemaNumber | FieldSchemaString => {
  return (
    schema.type === FieldSchemaType.Boolean ||
    schema.type === FieldSchemaType.Integer ||
    schema.type === FieldSchemaType.Number ||
    schema.type === FieldSchemaType.String
  );
};

const hasRequirePresenceConfig = (
  schema: FieldSchemaEditorSupportedTypes,
): schema is
  | FieldSchemaArray
  | FieldSchemaBoolean
  | FieldSchemaChoices
  | FieldSchemaDate
  | FieldSchemaEmailAddress
  | FieldSchemaFile
  | FieldSchemaHash
  | FieldSchemaInteger
  | FieldSchemaMultiChoices
  | FieldSchemaNumber
  | FieldSchemaString => {
  return (
    schema.type === FieldSchemaType.Array ||
    schema.type === FieldSchemaType.Boolean ||
    schema.type === FieldSchemaType.Choices ||
    schema.type === FieldSchemaType.Date ||
    schema.type === FieldSchemaType.EmailAddress ||
    schema.type === FieldSchemaType.File ||
    schema.type === FieldSchemaType.Hash ||
    schema.type === FieldSchemaType.Integer ||
    schema.type === FieldSchemaType.MultiChoices ||
    schema.type === FieldSchemaType.Number ||
    schema.type === FieldSchemaType.String
  );
};

const FieldSchemaPanel: React.VFC<FieldSchemaPanelProps> = ({
  isComputedFieldsEnabled,
  isRequirePresenceEnabled,
  allowHash = true,
  allowRelation = false,
  errorMessage,
  fieldSchema,
  fieldSchemaPath,
  relationObjectTypeOptions = [],
  identifierConfig,
  canConfigureIdentifier = false,
  isTopLevelField = false,
  isNewField,
  isSystemField = false,
  onCancel,
  onEditing,
  onUpdate,
  selectReason,
  systemFieldSchemas = [],
}) => {
  const [updatedFieldSchema, setUpdatedFieldSchema] =
    useState<FieldSchemaEditorSupportedTypes>(fieldSchema);
  const [updatedIdentifierConfig, setUpdatedIdentifierConfig] = useState(identifierConfig);

  const previousUpdatedFieldSchema = useRef<FieldSchemaEditorSupportedTypes>();
  const previousUpdatedIdentifier = useRef<Identifier>();

  const setUpdatedFieldSchemaWrapper = (fieldSchema: FieldSchemaEditorSupportedTypes) => {
    const systemField = systemFieldSchemas.find((schema) => schema.key === fieldSchema.key);
    if (systemField && isTopLevelField) {
      setUpdatedFieldSchema(systemField);
    } else {
      setUpdatedFieldSchema(fieldSchema);
    }
  };

  const fieldTypeOptions = useMemo(() => {
    return SUPPORTED_FIELD_TYPE_OPTIONS.sort((option1, option2) => {
      const option1Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option1];
      const option2Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option2];

      if (option1Name && option2Name) {
        return option1Name.localeCompare(option2Name);
      } else {
        return option1.localeCompare(option2);
      }
    })
      .filter(
        (option) =>
          allowHash ||
          option !== FieldSchemaType.Hash ||
          updatedFieldSchema.type === FieldSchemaType.Hash,
      )
      .filter(
        (option) =>
          allowRelation ||
          option !== RelationSchemaType.RelationSchema ||
          updatedFieldSchema.type === RelationSchemaType.RelationSchema,
      )
      .map((option) => (
        <Option key={option} value={option}>
          {FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option]}
        </Option>
      ));
  }, [allowHash, allowRelation, updatedFieldSchema]);

  const arrayTypeOptions = useMemo(() => {
    return SUPPORTED_ARRAY_TYPES_OPTIONS.sort((option1, option2) => {
      const option1Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option1];
      const option2Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option2];

      if (option1Name && option2Name) {
        return option1Name.localeCompare(option2Name);
      } else {
        return option1.localeCompare(option2);
      }
    })
      .filter(
        (option) =>
          allowHash ||
          option !== FieldSchemaType.Hash ||
          (updatedFieldSchema.type === FieldSchemaType.Array &&
            updatedFieldSchema.config['item-schema'].type === FieldSchemaType.Hash),
      )
      .map((option) => (
        <Option key={option} value={option}>
          {FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option]}
        </Option>
      ));
  }, [allowHash, updatedFieldSchema]);

  useEffect(() => {
    setUpdatedFieldSchema(fieldSchema);
  }, [fieldSchema]);

  useEffect(() => {
    if (
      isEqual(previousUpdatedFieldSchema.current, updatedFieldSchema) &&
      isEqual(previousUpdatedIdentifier.current === updatedIdentifierConfig)
    ) {
      return;
    }
    previousUpdatedFieldSchema.current = updatedFieldSchema;
    previousUpdatedIdentifier.current = updatedIdentifierConfig;
    onEditing(updatedFieldSchema, updatedIdentifierConfig);
  }, [updatedFieldSchema, updatedIdentifierConfig, onEditing]);

  const updateFieldSchemaIsComputed = (computed: boolean) => {
    if (hasComputedConfig(updatedFieldSchema)) {
      setUpdatedFieldSchema({
        ...updatedFieldSchema,
        config: {
          ...updatedFieldSchema.config,
          computed,
        },
      });
    }
  };

  const updateFieldSchemaIsRequired = (required: boolean) => {
    const newSchema = {
      ...updatedFieldSchema,
      config: {
        ...updatedFieldSchema.config,
        required,
      },
    };
    if (hasRequirePresenceConfig(updatedFieldSchema)) {
      setUpdatedFieldSchema(newSchema);
    }
  };

  function findRelatedObjectLabel(token: string) {
    for (const relationObject of relationObjectTypeOptions) {
      for (const option of relationObject.options ?? []) {
        if (option.value === token) {
          return option.label;
        }
      }
    }
  }

  return (
    <FieldSchemaPanelContainer role='form' aria-label='Field schema editor'>
      <Heading size='md'>{isNewField ? 'Add new field' : 'Edit Field'}</Heading>
      <Spacer height={2} />
      <FieldConfigurationSection>
        <Spacer height={2} />
        <Heading size='sm'>Details</Heading>
        <Spacer height={2} />
        {isNewField ? (
          <>
            <InputLabel htmlFor='field-schema-panel__field-label'>Field name</InputLabel>
            <Input
              value={updatedFieldSchema.label ? updatedFieldSchema.label : ''}
              placeholder='label'
              disabled={isSystemField && !isTopLevelField}
              onChange={(e) => {
                setUpdatedFieldSchemaWrapper({
                  ...updatedFieldSchema,
                  label: e.target.value,
                  key: snakeCase(e.target.value),
                });

                if (updatedIdentifierConfig)
                  setUpdatedIdentifierConfig({
                    ...updatedIdentifierConfig,
                    key: snakeCase(e.target.value),
                  });
              }}
              id='field-schema-panel__field-label'
            />
            <Spacer height={2} />
            <InputLabel htmlFor='field-schema-panel__field-key'>Key</InputLabel>
            <Input
              value={updatedFieldSchema.key ? updatedFieldSchema.key : '--'}
              placeholder='-'
              onChange={(e) => {
                setUpdatedFieldSchemaWrapper({
                  ...updatedFieldSchema,
                  key: e.target.value,
                });
              }}
              disabled={true}
              id='field-schema-panel__field-key'
            />
            {isSystemField && (
              <>
                <Spacer height={1} />
                <Paragraph>
                  {fieldSchemaPath ? `"${fieldSchemaPath}"` : 'This'} is a reserved key for system
                  fields.
                </Paragraph>
              </>
            )}
            <Spacer height={2} />
            <InputLabel htmlFor='field-schema-panel__field-type'>Field type</InputLabel>
            <Select
              value={updatedFieldSchema.type}
              disabled={!isNewField || isSystemField}
              id='field-schema-panel__field-type'
              maxHeight={200}
              onValueChange={(type: FieldSchemaType | RelationSchemaType) => {
                const config = type === FieldSchemaType.Hash ? { 'item-schemas': [{}] } : {};
                if (type === RelationSchemaType.RelationSchema) {
                  const cardinality = 'many';
                  setUpdatedFieldSchemaWrapper({
                    ...updatedFieldSchema,
                    type,
                    cardinality,
                  } as FieldSchemaRelationSchema);
                } else {
                  setUpdatedFieldSchemaWrapper({
                    ...updatedFieldSchema,
                    type,
                    config,
                  } as FieldSchema);
                }

                if (type !== FieldSchemaType.String) {
                  setUpdatedIdentifierConfig(undefined);
                }
              }}
            >
              {fieldTypeOptions}
            </Select>

            {updatedFieldSchema.type === FieldSchemaType.Array && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-array-type'>
                  Array item type
                </InputLabel>
                <Select
                  value={updatedFieldSchema.config['item-schema']?.type}
                  id='field-schema-panel__field-array-type'
                  disabled={isSystemField}
                  onValueChange={(type: FieldSchemaType) => {
                    const config = {
                      ...updatedFieldSchema.config,
                      'item-schema': {
                        type,
                        config: type === FieldSchemaType.Hash ? { 'item-schemas': [{}] } : {},
                      },
                    };
                    setUpdatedFieldSchemaWrapper({
                      ...updatedFieldSchema,
                      config,
                    } as FieldSchemaArray);
                  }}
                >
                  {arrayTypeOptions}
                </Select>
              </>
            )}

            {(updatedFieldSchema.type === FieldSchemaType.MultiChoices ||
              updatedFieldSchema.type === FieldSchemaType.Choices) && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-multi-choices-input'>
                  Options
                </InputLabel>
                <ReactCreatableSelect
                  size='md'
                  id='field-schema-panel__field-multi-choices'
                  placeholder='To create a new choice, start typing'
                  disabled={isSystemField}
                  value={updatedFieldSchema.config.options ?? []}
                  options={(updatedFieldSchema.config.options ?? []).map((value) => ({
                    value: value,
                    label: value,
                  }))}
                  onChange={(value: string) =>
                    setUpdatedFieldSchemaWrapper({
                      ...updatedFieldSchema,
                      config: {
                        ...updatedFieldSchema.config,
                        options: [...value],
                      },
                    })
                  }
                  isMulti
                  portal
                  menuPlacement='bottom'
                />
              </>
            )}

            {allowRelation && updatedFieldSchema.type === RelationSchemaType.RelationSchema && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-related-object-type'>
                  Related object
                </InputLabel>
                <ReactSelect
                  id='field-schema-panel__field-related-object-type'
                  aria-label='Related object'
                  disabled={isSystemField}
                  searchEnabled={true}
                  options={relationObjectTypeOptions}
                  value={updatedFieldSchema.token}
                  onChange={(value: string) => {
                    setUpdatedFieldSchemaWrapper({
                      ...updatedFieldSchema,
                      token: value,
                    });
                  }}
                />
                <Spacer height={2} />
                <Checkbox
                  label='Is an array'
                  disabled={isSystemField}
                  checked={updatedFieldSchema.cardinality === 'many'}
                  onChange={() => {
                    setUpdatedFieldSchemaWrapper({
                      ...updatedFieldSchema,
                      cardinality: updatedFieldSchema.cardinality === 'many' ? 'one' : 'many',
                    });
                  }}
                  data-test='field-schema-panel_cardinality-checkbox'
                />
              </>
            )}
            {allowRelation &&
              updatedFieldSchema.type === RelationSchemaType.RelationSchema &&
              updatedFieldSchema.token && (
                <>
                  <Spacer height={2} />

                  <Heading size='sm'>Add new field to related object</Heading>
                  <Paragraph>
                    Relation fields have bidirectional relationships. Add a new field to the related
                    object above to set up its relation to this account type.
                  </Paragraph>

                  <Spacer height={2} />

                  <InputLabel htmlFor='field-schema-panel__field-inverse-label'>
                    Field name
                  </InputLabel>
                  <Input
                    value={updatedFieldSchema['inverse-label'] ?? ''}
                    placeholder='label'
                    disabled={isSystemField}
                    onChange={(e) => {
                      setUpdatedFieldSchemaWrapper({
                        ...updatedFieldSchema,
                        'inverse-label': e.target.value,
                        'inverse-key': snakeCase(e.target.value),
                        'inverse-cardinality': 'one',
                      });
                    }}
                    id='field-schema-panel__field-inverse-label'
                    data-test='field-schema-panel__field-inverse-label'
                  />
                  <Spacer height={2} />
                  <InputLabel htmlFor='field-schema-panel__field-inverse-key'>Key</InputLabel>
                  <Input
                    value={updatedFieldSchema['inverse-key'] ?? '--'}
                    placeholder='-'
                    disabled={isSystemField}
                    onChange={(e) => {
                      setUpdatedFieldSchemaWrapper({
                        ...updatedFieldSchema,
                        'inverse-key': e.target.value,
                      });
                    }}
                    id='field-schema-panel__field-inverse-key'
                    data-test='field-schema-panel__field-inverse-key'
                  />
                  <Spacer height={2} />
                  <Checkbox
                    label='Is an array'
                    disabled={isSystemField}
                    checked={updatedFieldSchema['inverse-cardinality'] === 'many'}
                    onChange={() => {
                      setUpdatedFieldSchemaWrapper({
                        ...updatedFieldSchema,
                        'inverse-cardinality':
                          updatedFieldSchema['inverse-cardinality'] === 'many' ? 'one' : 'many',
                      });
                    }}
                    data-test='field-schema-panel_inverse-cardinality-checkbox'
                  />
                </>
              )}
            {isComputedFieldsEnabled && hasComputedConfig(updatedFieldSchema) && (
              <>
                <Spacer height={2} />
                <Checkbox
                  disabled={isSystemField}
                  label='Computed value'
                  checked={
                    'computed' in updatedFieldSchema.config && updatedFieldSchema.config['computed']
                  }
                  onChange={() =>
                    updateFieldSchemaIsComputed(!updatedFieldSchema.config['computed'])
                  }
                  data-test='field-schema-panel_computed-field-checkbox'
                />
              </>
            )}
            {isRequirePresenceEnabled && hasRequirePresenceConfig(updatedFieldSchema) && (
              <>
                <Spacer height={2} />
                <FieldConfigurationSection>
                  <Spacer height={2} />
                  <Heading size='sm'>Configurations</Heading>
                  <Spacer height={2} />
                  <Paragraph>
                    Changing these configurations will only affect new transactions created
                  </Paragraph>
                  <Spacer height={2} />
                  <RequireCheckboxContainer>
                    <Checkbox
                      label={
                        updatedFieldSchema.type === FieldSchemaType.Array ||
                        updatedFieldSchema.type === FieldSchemaType.MultiChoices
                          ? 'Require at least one item'
                          : 'Require field'
                      }
                      disabled={isSystemField}
                      checked={updatedFieldSchema.config.required}
                      onChange={() =>
                        updateFieldSchemaIsRequired(!updatedFieldSchema.config.required)
                      }
                      data-test='field-schema-panel_require-field-checkbox'
                    />
                    <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
                      <Paragraph>
                        Required fields must be populated for the transaction to be created
                      </Paragraph>
                    </Tooltip>
                  </RequireCheckboxContainer>
                </FieldConfigurationSection>
              </>
            )}

            {canConfigureIdentifier &&
              isTopLevelField &&
              updatedFieldSchema.type === FieldSchemaType.String && (
                <>
                  <Spacer height={2} />
                  <FieldConfigurationSection>
                    <Spacer height={2} />
                    <Heading size='sm'>Configurations</Heading>
                    <Spacer height={2} />
                    <RequireCheckboxContainer>
                      <Checkbox
                        label='Identifier'
                        checked={updatedIdentifierConfig?.key === updatedFieldSchema.key}
                        onChange={() => {
                          if (updatedIdentifierConfig?.key === updatedFieldSchema.key) {
                            setUpdatedIdentifierConfig(undefined);
                          } else {
                            setUpdatedIdentifierConfig({
                              key: updatedFieldSchema.key,
                              config: {
                                ['case-sensitive']: false,
                                unique: false,
                              },
                            });
                          }
                        }}
                      />
                      <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
                        <TooltipText>
                          Use this field to store an external ID that can be used to do exact
                          lookups within Persona. This setting is currently only applicable to top
                          level string fields.
                        </TooltipText>
                      </Tooltip>
                    </RequireCheckboxContainer>
                    {updatedIdentifierConfig && (
                      <RequireCheckboxContainer>
                        <Spacer height={2} />
                        <Checkbox
                          label='Case Sensitive'
                          checked={updatedIdentifierConfig?.config?.['case-sensitive']}
                          onChange={() => {
                            setUpdatedIdentifierConfig({
                              ...updatedIdentifierConfig,
                              config: {
                                ...updatedIdentifierConfig.config,
                                ['case-sensitive']:
                                  !updatedIdentifierConfig?.config?.['case-sensitive'],
                              },
                            });
                          }}
                        />
                        <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
                          <TooltipText>
                            ‘Case sensitive’ is a field setting that treats &quot;ABC&quot; and
                            &quot;abc&quot; as different values for uniqueness.
                          </TooltipText>
                        </Tooltip>
                      </RequireCheckboxContainer>
                    )}
                    {updatedIdentifierConfig && (
                      <RequireCheckboxContainer>
                        <Checkbox
                          label='Unique'
                          checked={updatedIdentifierConfig?.config?.['unique']}
                          onChange={() => {
                            setUpdatedIdentifierConfig({
                              ...updatedIdentifierConfig,
                              config: {
                                ...updatedIdentifierConfig.config,
                                ['unique']: !updatedIdentifierConfig?.config?.['unique'],
                              },
                            });
                          }}
                        />
                        <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
                          <TooltipText>
                            ‘Unique’ is a field setting that prevents the same value from being used
                            in multiple records for this field.
                          </TooltipText>
                        </Tooltip>
                      </RequireCheckboxContainer>
                    )}
                  </FieldConfigurationSection>
                </>
              )}
          </>
        ) : (
          <>
            <StyledAttributeGrid colCount={2}>
              <AttributeItem
                title='Field name'
                value={updatedFieldSchema.label ? updatedFieldSchema.label : '--'}
                dataTest='field-details__label'
                titleSpacing={4}
              />
              <AttributeItem
                title='Key'
                value={updatedFieldSchema.key ? updatedFieldSchema.key : '--'}
                dataTest='field-details__key'
                titleSpacing={4}
                wordBreak='break-all'
              />
              <AttributeItem
                title='Field type'
                value={capitalize(
                  updatedFieldSchema.type === 'relation-schema'
                    ? 'relation'
                    : updatedFieldSchema.type,
                )}
                dataTest='field-details__type'
                titleSpacing={4}
                wordBreak='break-all'
              />
              {identifierConfig && (
                <AttributeItem
                  title='Identifier'
                  value={`${identifierConfig.config.unique ? 'Unique' : 'Non-unique'}, ${identifierConfig.config['case-sensitive'] ? 'case sensitive' : 'case insensitive'}`}
                  dataTest='field-details__identifier'
                  titleSpacing={4}
                  wordBreak='break-all'
                />
              )}
              {updatedFieldSchema.type === FieldSchemaType.Array && (
                <AttributeItem
                  title='Array item type'
                  value={capitalize(updatedFieldSchema.config['item-schema']?.type)}
                  dataTest='field-details__array-item-type'
                  titleSpacing={4}
                  wordBreak='break-all'
                />
              )}
              {allowRelation && updatedFieldSchema.type === RelationSchemaType.RelationSchema && (
                <>
                  <AttributeItem
                    title='Related object'
                    value={
                      updatedFieldSchema.cardinality === 'many'
                        ? `${findRelatedObjectLabel(updatedFieldSchema.token)} (Array)`
                        : findRelatedObjectLabel(updatedFieldSchema.token)
                    }
                    dataTest='field-details__related-object-type'
                    titleSpacing={4}
                    wordBreak='break-all'
                  />
                  <AttributeItem
                    title='Related object name'
                    value={updatedFieldSchema['inverse-label']}
                    dataTest='field-details__related-object-name'
                    titleSpacing={4}
                    wordBreak='break-all'
                  />
                  <AttributeItem
                    title='Related object key'
                    value={updatedFieldSchema['inverse-key']}
                    dataTest='field-details__related-object-key'
                    titleSpacing={4}
                    wordBreak='break-all'
                  />
                </>
              )}
            </StyledAttributeGrid>
            {(updatedFieldSchema.type === FieldSchemaType.MultiChoices ||
              updatedFieldSchema.type === FieldSchemaType.Choices) && (
              <>
                <Spacer height={2} />
                <AttributeItem
                  title='Options'
                  value={
                    <StickerContainer>
                      {updatedFieldSchema.config.options &&
                        updatedFieldSchema.config.options.map((option) => (
                          <Sticker key={option} data-test={`field-details__options--${option}`}>
                            {option}
                          </Sticker>
                        ))}
                    </StickerContainer>
                  }
                  dataTest='field-details__options'
                  titleSpacing={4}
                />
              </>
            )}
            {isComputedFieldsEnabled && hasComputedConfig(updatedFieldSchema) && (
              <>
                <Spacer height={2} />
                <Checkbox
                  label='Computed value'
                  checked={
                    'computed' in updatedFieldSchema.config && updatedFieldSchema.config['computed']
                  }
                  onChange={() =>
                    updateFieldSchemaIsComputed(!updatedFieldSchema.config['computed'])
                  }
                  data-test='field-schema-panel_computed-field-checkbox'
                  disabled={true}
                />
              </>
            )}
            {isRequirePresenceEnabled && hasRequirePresenceConfig(updatedFieldSchema) && (
              <>
                <Spacer height={2} />
                <FieldConfigurationSection>
                  <Spacer height={2} />
                  <Heading size='sm'>Configurations</Heading>
                  <Spacer height={2} />
                  <Paragraph>
                    Changing these configurations will only affect new transactions created
                  </Paragraph>
                  <Spacer height={2} />
                  <RequireCheckboxContainer>
                    <Checkbox
                      label={
                        updatedFieldSchema.type === FieldSchemaType.Array ||
                        updatedFieldSchema.type === FieldSchemaType.MultiChoices
                          ? 'Require at least one item'
                          : 'Require field'
                      }
                      checked={updatedFieldSchema.config.required}
                      onChange={() =>
                        updateFieldSchemaIsRequired(!updatedFieldSchema.config.required)
                      }
                      data-test='field-schema-panel_require-field-checkbox'
                    />
                    <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
                      <Paragraph>
                        Required fields must be populated for the transaction to be created
                      </Paragraph>
                    </Tooltip>
                  </RequireCheckboxContainer>
                </FieldConfigurationSection>
              </>
            )}
          </>
        )}

        {errorMessage && <FormError>{errorMessage}</FormError>}
      </FieldConfigurationSection>
      <FieldConfigurationControl>
        <FieldButtonGroup spacing={Spacing[2]}>
          {(isNewField || isRequirePresenceEnabled) && (
            <Button
              id='field-schema-panel__add'
              data-test='field-schema-panel__add'
              variant='primary'
              disabled={!!errorMessage || !(isRequirePresenceEnabled || isNewField)}
              onClick={() => {
                setUpdatedFieldSchemaWrapper(updatedFieldSchema);
                onUpdate(updatedFieldSchema, updatedIdentifierConfig);
              }}
            >
              {FOCUS_REASON_TO_ACTION[selectReason]
                ? FOCUS_REASON_TO_ACTION[selectReason]
                : 'Confirm'}
            </Button>
          )}
          <Button
            id='field-schema-panel__cancel'
            data-test='field-schema-panel__cancel'
            onClick={onCancel}
            variant='basic'
          >
            Cancel
          </Button>
        </FieldButtonGroup>
      </FieldConfigurationControl>
    </FieldSchemaPanelContainer>
  );
};

export default FieldSchemaPanel;
